// Drag-to-add (05h): the pure part of main.js's side-panel drag. It covers what a tile
// carries, where the ghost snaps, when a press becomes a drag, and what the drop does.
// The live drag is menu-drag.ts.

/** The side rail's width. A pointer over it shows no outline, and a drop there cancels. */
export const SIDE_RAIL_WIDTH = 56;
/** The canvas origin's distance from the top of the page (main.js's `pageY - 16`). */
export const CANVAS_TOP = 16;
/** A press becomes a drag once it travels past this many px in either direction. */
export const MENU_DRAG_THRESHOLD = 3;
/** A saved-dashboard card's ghost: a fixed placeholder size (its drop never reads it). */
export const PRESET_GHOST_COLS = 6;
export const PRESET_GHOST_ROWS = 3;

/**
 * A tile's pre-seeded state: every `data-context-*` attribute, kebab → camel with a `_`
 * in front (`data-context-ranking-category-id` → `_rankingCategoryId`). Several tiles can
 * share one module this way ("Passing Rank", "Attacking Rank"… are all a Ranking). Null
 * when the tile carries none.
 */
export function readTemplateContext(attrs: Iterable<{ name: string; value: string }>): Record<string, string> | null {
  const context: Record<string, string> = {};
  for (const attr of attrs) {
    if (attr.name.indexOf("data-context-") !== 0) continue;
    const camel = attr.name.slice("data-context-".length).replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    context[`_${camel}`] = attr.value;
  }
  return Object.keys(context).length ? context : null;
}

/** Past the threshold: a real drag now, not a press. */
export function isMenuDragStart(dx: number, dy: number): boolean {
  return Math.abs(dx) > MENU_DRAG_THRESHOLD || Math.abs(dy) > MENU_DRAG_THRESHOLD;
}

/** The canvas point the ghost snaps from: the top-left of a base-size box centred on the
 *  pointer, in unscaled canvas px (then `snapCoords` picks the cell). */
export function ghostSnapPoint(
  pageX: number,
  pageY: number,
  scale: number,
  baseW: number,
  baseH: number,
): { x: number; y: number } {
  return {
    x: (pageX - SIDE_RAIL_WIDTH) / scale - baseW / 2,
    y: (pageY - CANVAS_TOP) / scale - baseH / 2,
  };
}

/** What a release does. */
export type MenuDropKind =
  /** A saved-dashboard card over the canvas: load that dashboard. */
  | "load-preset"
  /** A player onto a Shot Map / Pass Map / Passing Profile / Heat Map: switch its player. */
  | "switch"
  /** A player onto Similarity: its overlay. */
  | "overlay"
  /** A player onto a Radar / Stats: a same-type module for them joins it. */
  | "token-merge"
  /** A player onto a Match module: their latest game. */
  | "player-match"
  /** A player onto a League Table / Squad / Match Results: it opens on their league and club. */
  | "player-league"
  /** A match onto a player's map (Shot / Pass / Heat Map, Passing Profile / Report): it narrows to that game. */
  | "match-map"
  /** A match onto a Match module: it shows that match. */
  | "match-switch"
  /** A team onto a module that takes teams: the Bio shows it; League Table, Squad, Match Results open on it. */
  | "team-switch"
  /** A tile onto a module it combines with: merge. */
  | "merge"
  /** A tile, player, team or match onto a free cell: a new module there (a player or team makes a Bio, a match a Match). */
  | "place"
  /** Never really dragged, back over the rail, or red: nothing is added. */
  | "cancel";

export interface MenuDropInput {
  kind: "module" | "player" | "team" | "match" | "preset";
  presetKey: string | null;
  /** Travelled past the threshold. */
  moved: boolean;
  /** Released over the side rail. */
  overRail: boolean;
  /** The outline was red. */
  invalid: boolean;
  /** The module it lands on (only looked up when the outline was blue), as the drop needs it. */
  target: { isSwitch: boolean; isOverlay: boolean; isLeague?: boolean; isMatch?: boolean; isMap?: boolean } | null;
}

/** main.js's pointerup branches, in its order. */
export function menuDrop(i: MenuDropInput): MenuDropKind {
  if (i.kind === "preset") return i.moved && !i.overRail && i.presetKey ? "load-preset" : "cancel";
  if (!i.moved || i.overRail || i.invalid) return "cancel";
  if (i.kind === "player" && i.target) {
    // A switch and the overlay come before the generic merge: neither a map nor Player
    // Similarity has a comparison mode to join.
    if (i.target.isLeague) return "player-league";
    if (i.target.isMatch) return "player-match";
    return i.target.isSwitch ? "switch" : i.target.isOverlay ? "overlay" : "token-merge";
  }
  // A team is only ever blue over a module that shows teams (menu-drag.ts), so a target = a switch
  // (or, on a Stats tile showing a team, a join: team-merge.ts).
  if (i.kind === "team") return i.target ? "team-switch" : "place";
  // The same for a match, over a Match module.
  if (i.kind === "match") return i.target ? (i.target.isMap ? "match-map" : "match-switch") : "place";
  return i.target ? "merge" : "place";
}
